import clientApi from "../client";
import { appendDateRange } from "@/lib/date-utils";

export interface ManagerTeamDimensionMetric {
  dimension_key: string;
  dimension_name: string;
  avg_score: number;
  description: string;
}

export interface ManagerTeamStatsResponse {
  avg_prep_score: number;
  avg_delivery_score: number;
  prep_call_count: number;
  delivery_call_count: number;
  last_week_avg_score: number;
  last_week_calls: number;
  last_week_map_percentage: number;
  top_strength: ManagerTeamDimensionMetric | null;
  focus_area: ManagerTeamDimensionMetric | null;
  prep_trend_wow: number;
  delivery_trend_wow: number;
}

export interface GetManagerTeamStatsParams {
  managerEmail?: string | null;
  startDate?: string;
  endDate?: string;
}

export interface ManagerRepDealHealth {
  progressing: number;
  watch: number;
  stalling: number;
  stuck: number;
  summary: string;
}

export interface ManagerRepItem {
  rep_email: string;
  rep_name: string;
  title: string | null;
  avatar_initials: string;
  avg_prep: number;
  avg_delivery: number;
  top_outcome: string | null;
  top_strength: ManagerTeamDimensionMetric | null;
  focus_area: ManagerTeamDimensionMetric | null;
  deal_health: ManagerRepDealHealth;
  calls: number;
}

export interface GetManagerRepsParams {
  managerEmail?: string | null;
  startDate?: string;
  endDate?: string;
  repName?: string;
  page?: number;
  pageSize?: number;
  sortDir?: "asc" | "desc";
}

export interface ManagerRepsResponse {
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  data: ManagerRepItem[];
}

export interface ManagerRepOptionItem {
  label: string;
  value: string;
  email: string;
}

export interface ManagerRepOptionsResponse {
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  data: ManagerRepOptionItem[];
}

export interface GetManagerRepOptionsParams {
  managerEmail?: string | null;
  page?: number;
  pageSize?: number;
}

export interface ManagerMomentumBreakdown {
  Stuck: number;
  Stalling: number;
  Watch: number;
  Progressing: number;
}

export interface ManagerDirectionBreakdown {
  improving: number;
  steady: number;
  declining: number;
}

export interface ManagerMostAtRisk {
  account_id?: number;
  account_name?: string;
  rep_name?: string;
  state?: string;
}

export interface ManagerAccountStatsResponse {
  total_active_accounts: number;
  rep_count: number;
  momentum_breakdown: ManagerMomentumBreakdown;
  direction_breakdown: ManagerDirectionBreakdown;
  most_at_risk: ManagerMostAtRisk | null;
  needs_attention_count: number;
}

export interface GetManagerAccountStatsParams {
  managerEmail?: string | null;
  startDate?: string;
  endDate?: string;
}

export interface ManagerAccountApiItem {
  account_id: number;
  account_name: string;
  image: string | null;
  rep_email: string;
  rep_display_name: string;
  momentum_label: string | null;
  momentum_direction: string | null;
  last_call_date: string | null;
  calls: number;
  avg_prep: number;
  avg_delivery: number;
  outcome: string | null;
  has_call_in_range: boolean;
}

export interface ManagerAccountsResponse {
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  data: ManagerAccountApiItem[];
}

export interface GetManagerAccountsParams {
  managerEmail?: string | null;
  startDate?: string;
  endDate?: string;
  search?: string;
  repEmail?: string;
  momentumState?: string;
  direction?: string;
  page?: number;
  pageSize?: number;
  sortDir?: "asc" | "desc";
}

export interface ManagerCoachingFocusItem {
  dimension_key: string;
  dimension_name: string;
  source: string;
  action: string;
  tip: string;
  score: number;
  frequency_count: number;
  call_id: number;
  call_date: string;
  dimension: string;
  label: string;
  area: string;
  count: number;
  description: string;
}

export interface GetManagerCoachingFocusParams {
  accountId: number;
  repEmail: string;
}

export interface GetManagerRepCoachingFocusParams {
  repEmail: string;
}

export interface ManagerCoachingEmailResponse {
  subject: string;
  body: string;
}

export interface GetManagerAccountCoachingEmailParams {
  accountId: number;
  repEmail: string;
}

export interface GetManagerRepCoachingEmailParams {
  repEmail: string;
}

export interface ManagerRepBlockerItem {
  key: string;
  label: string;
  reason: string;
}

export interface ManagerRepBlockerAccount {
  account_id: number;
  account_name: string;
  momentum_label: string;
  momentum_direction: string;
  latest_meeting_id: number;
  latest_meeting_date: string;
  active_indicator_count: number;
  blockers: ManagerRepBlockerItem[];
}

export interface ManagerRepBlockersResponse {
  rep_email: string;
  rep_name: string;
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  account_count_with_blockers: number;
  accounts: ManagerRepBlockerAccount[];
  empty_state: string;
  disclaimer: string;
}

export interface GetManagerRepBlockersParams {
  repEmail: string;
  page?: number;
  pageSize?: number;
}

function appendManagerEmail(
  params: URLSearchParams,
  managerEmail?: string | null,
) {
  const trimmed = managerEmail?.trim();
  if (trimmed) {
    params.set("manager_email", trimmed);
  }
}

function managerBaseParams(managerEmail?: string | null) {
  const params = new URLSearchParams();
  appendManagerEmail(params, managerEmail);
  return params;
}

export class ManagerService {
  static async getTeamStats({
    managerEmail,
    startDate,
    endDate,
  }: GetManagerTeamStatsParams): Promise<ManagerTeamStatsResponse> {
    const params = managerBaseParams(managerEmail);
    appendDateRange(params, startDate, endDate);

    return clientApi
      .get(`api/manager/team-stats?${params.toString()}`)
      .json<ManagerTeamStatsResponse>();
  }

  static async getRepOptions({
    managerEmail,
    page = 1,
    pageSize = 50,
  }: GetManagerRepOptionsParams): Promise<ManagerRepOptionsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    appendManagerEmail(params, managerEmail);

    return clientApi
      .get(`api/manager/reps/options?${params.toString()}`)
      .json<ManagerRepOptionsResponse>();
  }

  static async getReps({
    managerEmail,
    startDate,
    endDate,
    repName,
    page = 1,
    pageSize = 20,
    sortDir = "asc",
  }: GetManagerRepsParams): Promise<ManagerRepsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      sort_dir: sortDir,
    });
    appendManagerEmail(params, managerEmail);
    appendDateRange(params, startDate, endDate);
    if (repName?.trim()) params.set("rep_name", repName.trim());

    return clientApi
      .get(`api/manager/reps?${params.toString()}`)
      .json<ManagerRepsResponse>();
  }

  static async getAccountStats({
    managerEmail,
    startDate,
    endDate,
  }: GetManagerAccountStatsParams): Promise<ManagerAccountStatsResponse> {
    const params = managerBaseParams(managerEmail);
    appendDateRange(params, startDate, endDate);

    return clientApi
      .get(`api/manager/account-stats?${params.toString()}`)
      .json<ManagerAccountStatsResponse>();
  }

  static async getAccounts({
    managerEmail,
    startDate,
    endDate,
    search,
    repEmail,
    momentumState,
    direction,
    page = 1,
    pageSize = 20,
    sortDir = "asc",
  }: GetManagerAccountsParams): Promise<ManagerAccountsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      sort_dir: sortDir,
    });
    appendManagerEmail(params, managerEmail);
    appendDateRange(params, startDate, endDate);
    if (search?.trim()) params.set("search", search.trim());
    if (repEmail?.trim()) params.set("rep_email", repEmail.trim());
    if (momentumState?.trim()) params.set("momentum_state", momentumState.trim());
    if (direction?.trim()) params.set("direction", direction.trim());

    return clientApi
      .get(`api/manager/accounts?${params.toString()}`)
      .json<ManagerAccountsResponse>();
  }

  static async getCoachingFocus({
    accountId,
    repEmail,
  }: GetManagerCoachingFocusParams): Promise<ManagerCoachingFocusItem[]> {
    const params = new URLSearchParams({ rep_email: repEmail.trim() });

    return clientApi
      .get(`api/manager/account/${accountId}/coaching-focus?${params.toString()}`)
      .json<ManagerCoachingFocusItem[]>();
  }

  static async getRepCoachingFocus({
    repEmail,
  }: GetManagerRepCoachingFocusParams): Promise<ManagerCoachingFocusItem[]> {
    return clientApi
      .get(`api/manager/rep/${encodeURIComponent(repEmail.trim())}/coaching-focus`)
      .json<ManagerCoachingFocusItem[]>();
  }

  static async getAccountCoachingEmail({
    accountId,
    repEmail,
  }: GetManagerAccountCoachingEmailParams): Promise<ManagerCoachingEmailResponse> {
    const params = new URLSearchParams({ rep_email: repEmail.trim() });

    return clientApi
      .get(`api/manager/account/${accountId}/coaching-email?${params.toString()}`)
      .json<ManagerCoachingEmailResponse>();
  }

  static async getRepCoachingEmail({
    repEmail,
  }: GetManagerRepCoachingEmailParams): Promise<ManagerCoachingEmailResponse> {
    return clientApi
      .get(`api/manager/rep/${encodeURIComponent(repEmail.trim())}/coaching-email`)
      .json<ManagerCoachingEmailResponse>();
  }

  static async getRepBlockers({
    repEmail,
    page = 1,
    pageSize = 10,
  }: GetManagerRepBlockersParams): Promise<ManagerRepBlockersResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });

    return clientApi
      .get(`api/manager/reps/${encodeURIComponent(repEmail.trim())}/blockers?${params.toString()}`)
      .json<ManagerRepBlockersResponse>();
  }
}
